<div class="sr-pop" [attr.data-testid]="base()" [attr.aria-busy]="saving()">
  @if (data()) {
    <div class="sr-pop-head close-gesture-handler">
      <h2 class="sr-pop-title" [id]="titleId()" [attr.aria-label]="dialogName()">
        {{ 'Special requests' | translate }}
        <span class="sr-pop-chip" data-testid="special-requests-popup-chip">{{
          'Room {n}' | translate: { n: roomNo() }
        }}</span>
      </h2>
      <button
        type="button"
        class="sr-pop-x"
        [attr.data-testid]="base() + '-close'"
        [attr.aria-label]="'Close' | translate"
        [disabled]="saving()"
        (click)="cancel()"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
          <path
            d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
          />
        </svg>
      </button>
    </div>
  }

  @if (error(); as e) {
    @switch (e.box) {
      @case ('save') {
        <app-special-requests-error-box
          [ids]="{
            box: 'special-requests-save-error',
            code: 'special-requests-save-error-code',
            trace: 'special-requests-save-trace-id',
            copy: 'special-requests-save-trace-copy',
            retry: e.canRetry ? 'special-requests-save-retry' : undefined
          }"
          [message]="e.message"
          [code]="e.error.code"
          [traceId]="e.error.traceId"
          [retrying]="saving()"
          (retry)="save()"
        />
      }
      @case ('changed') {
        <app-special-requests-error-box
          message="The special requests were changed by someone else. The current text is shown - check it and save again."
          [ids]="{
            box: 'special-requests-changed-error',
            code: 'special-requests-changed-error-code',
            trace: 'special-requests-changed-trace-id',
            copy: 'special-requests-changed-trace-copy'
          }"
          [code]="e.error.code"
          [traceId]="e.error.traceId"
        />
        @if (current(); as c) {
          <p class="sr-pop-current" data-testid="special-requests-popup-current">
            <b>{{ 'Current' | translate }}:</b> {{ c }}
          </p>
        }
      }
      @case ('rate') {
        <app-special-requests-error-box
          message="Too many attempts. Wait a minute and try again."
          [ids]="{
            box: 'special-requests-rate-limited',
            code: 'special-requests-rate-error-code',
            trace: 'special-requests-rate-trace-id',
            copy: 'special-requests-rate-trace-copy'
          }"
          [code]="e.error.code"
          [traceId]="e.error.traceId"
        />
      }
    }
  }

  @if (data()) {
    <div class="sr-pop-card" data-testid="special-requests-popup-room">
      <span class="sr-pop-card-ico" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" fill-rule="evenodd">
          <path [attr.d]="iconPaths.hotel" />
        </svg>
      </span>
      <div class="sr-pop-card-text">
        <div class="sr-pop-card-title" data-testid="special-requests-popup-card-title">
          <span>{{ card().title }}</span>
          @if (stars(); as n) {
            <app-stars [stars]="n" />
          }
        </div>
        @if (card().dates) {
          <div class="sr-pop-card-line" data-testid="special-requests-popup-card-dates">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
              <path [attr.d]="iconPaths.calendar" />
            </svg>
            <span
              >{{ card().dates }}
              @if (card().nights; as n) {
                · {{ 'SR_NIGHTS_PLURAL' | translate: { value: n } }}
              }
            </span>
          </div>
        }
        @if (card().meta.length) {
          <div class="sr-pop-card-line" data-testid="special-requests-popup-card-meta">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
              <path [attr.d]="iconPaths.bed" />
            </svg>
            <span>{{ card().meta.join(' · ') }}</span>
          </div>
        }
      </div>
    </div>
  }

  <fieldset class="sr-pop-fs" [disabled]="saving()">
    @if (items().length) {
      <fieldset class="sr-pop-fs" data-testid="special-requests-popup-items">
        <legend>
          {{ 'Requests' | translate }}<small>{{ 'Tick what the guests would like' | translate }}</small>
        </legend>
        <div class="sr-pop-tiles">
          @for (it of items(); track it.typeId) {
            <label
              class="sr-pop-tile"
              [class.sr-pop-tile-on]="ticked(it.typeId)"
              [attr.for]="'sr-it-' + it.typeId"
              [attr.data-testid]="'special-requests-popup-item-' + it.typeId + '-tile'"
            >
              <input
                type="checkbox"
                class="sr-pop-hidden"
                [id]="'sr-it-' + it.typeId"
                [attr.data-testid]="'special-requests-popup-item-' + it.typeId"
                [checked]="ticked(it.typeId)"
                (change)="onToggle(it.typeId, $event)"
              />
              <svg
                class="sr-pop-tile-ico"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
                fill-rule="evenodd"
                aria-hidden="true"
              >
                <path [attr.d]="iconPaths[iconOf(it)]" />
              </svg>
              <span class="sr-pop-tile-name">
                {{ name(it) }}
                @if (it.retired) {
                  <small
                    class="sr-pop-retired"
                    [attr.data-testid]="'special-requests-popup-item-' + it.typeId + '-retired'"
                    >{{ 'This item is no longer offered - untick it.' | translate }}</small
                  >
                }
              </span>
              <span class="sr-pop-box" aria-hidden="true">
                @if (ticked(it.typeId)) {
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                    <path [attr.d]="iconPaths.check" />
                  </svg>
                }
              </span>
            </label>
          }
        </div>
      </fieldset>
    }

    <fieldset class="sr-pop-fs" data-testid="room-link">
      <legend>{{ 'Link this room to' | translate }}</legend>
      <div class="sr-pop-seg">
        <label class="sr-pop-seg-opt" for="room-link-none">
          <input
            type="radio"
            class="sr-pop-hidden"
            name="room-link"
            id="room-link-none"
            value="none"
            data-testid="room-link-none"
            [checked]="draft().linkKind === 'none'"
            (change)="setLinkKind('none')"
          />
          <span>{{ 'No link' | translate }}</span>
        </label>
        @if (data()?.otherRooms?.length) {
          <label class="sr-pop-seg-opt" for="room-link-room">
            <input
              type="radio"
              class="sr-pop-hidden"
              name="room-link"
              id="room-link-room"
              value="room"
              data-testid="room-link-room"
              [checked]="draft().linkKind === 'room'"
              (change)="setLinkKind('room')"
            />
            <span>{{ 'A room of this reservation' | translate }}</span>
          </label>
        }
        <label class="sr-pop-seg-opt" for="room-link-booking">
          <input
            type="radio"
            class="sr-pop-hidden"
            name="room-link"
            id="room-link-booking"
            value="booking"
            data-testid="room-link-booking"
            [checked]="draft().linkKind === 'booking'"
            (change)="setLinkKind('booking')"
          />
          <span>{{ 'Another reservation' | translate }}</span>
        </label>
      </div>

      @if (draft().linkKind === 'room') {
        <div class="sr-pop-sub">
          <label for="room-link-room-select">{{ 'Linked room' | translate }}</label>
          <div class="ie-input flex flex-1 flex-col">
            <div
              class="st:h-11 h-10 flex items-center bg-ie-bg-default border-1 border-ie-b-default rounded-sm hover:border-ie-b-focused px-2 focus-within:border-ie-b-focused"
            >
              <select
                id="room-link-room-select"
                class="outline-none border-none bg-transparent h-full w-full text-sm"
                data-testid="room-link-room-select"
                (change)="onLinkRoom($event)"
              >
                @for (o of data()?.otherRooms ?? []; track o.key) {
                  <option [value]="o.key" [selected]="draft().linkRoomKey === o.key">
                    {{ 'Room {n}' | translate: { n: o.roomNo } }}{{ o.label ? ' · ' + o.label : '' }}
                  </option>
                }
              </select>
            </div>
          </div>
        </div>
      }

      @if (draft().linkKind === 'booking') {
        <div class="sr-pop-sub">
          <label for="room-link-booking-input">{{ 'Reservation number' | translate }}</label>
          <div class="ie-input flex flex-1 flex-col" [class.sr-pop-invalid]="!!linkError()">
            <div
              class="st:h-11 h-10 flex items-center bg-ie-bg-default border-1 border-ie-b-default rounded-sm hover:border-ie-b-focused px-2 focus-within:border-ie-b-focused"
            >
              <input
                id="room-link-booking-input"
                type="text"
                inputmode="numeric"
                class="outline-none border-none bg-transparent h-full w-full"
                data-testid="room-link-booking-input"
                [attr.maxlength]="bookingMax"
                [placeholder]="'For example: 124512' | translate"
                [value]="draft().bookingNumber"
                [attr.aria-invalid]="linkError() ? 'true' : null"
                [attr.aria-describedby]="linkError() ? 'room-link-error' : null"
                (input)="onBookingNumber($event)"
              />
            </div>
          </div>
        </div>
        @if (linkError(); as le) {
          <p class="sr-pop-err" id="room-link-error" role="alert" data-testid="room-link-error">
            <span aria-hidden="true">! </span
            >{{ 'Enter the number of another reservation of your agency.' | translate }}
            <span class="sr-pop-code"
              >({{ 'Error code' | translate }}: <span data-testid="room-link-error-code">{{ le.code }}</span>
              @if (linkTrace(); as id) {
                ; {{ 'Trace ID' | translate }}: <span data-testid="room-link-error-trace-id">{{ id }}</span>
              }
              )</span
            >
            @if (linkTrace(); as id) {
              <button
                type="button"
                class="sr-pop-copy"
                data-testid="room-link-error-trace-copy"
                [attr.aria-label]="'Copy Trace ID to clipboard' | translate"
                (click)="copyLinkTrace(id)"
              >
                {{ (linkCopied() === id ? 'Copied' : 'Copy') | translate }}
              </button>
            }
          </p>
        }
      }
      @if (draft().linkKind !== 'none') {
        <p class="sr-pop-linkhint" data-testid="special-requests-popup-link-hint">
          {{ 'The hotel will try to place linked rooms close to each other.' | translate }}
        </p>
      }
    </fieldset>

    @if (storedWishes(); as w) {
      <p class="sr-pop-wishes" data-testid="special-requests-popup-other-wishes-readonly">
        {{ 'Other wishes: «{text}»' | translate: { text: w } }}
      </p>
    }
  </fieldset>

  <p class="sr-pop-hint" data-testid="special-requests-popup-hint">
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path [attr.d]="iconPaths.info" />
    </svg>
    <span>{{ 'The hotel tries to meet the requests but cannot guarantee them.' | translate }}</span>
  </p>
</div>

<ng-template #modalFooterTemplateRef>
  <div class="sr-pop-footer" data-qa="special-requests-popup-footer">
    <span class="sr-pop-count" aria-live="polite" data-testid="special-requests-popup-selected-count">{{
      selectedCount() ? ('{n} selected' | translate: { n: selectedCount() }) : ('Nothing selected' | translate)
    }}</span>
    <button
      mat-button
      type="button"
      class="sr-pop-cancel"
      data-testid="special-requests-popup-cancel"
      [disabled]="saving()"
      (click)="cancel()"
    >
      {{ 'Cancel' | translate }}
    </button>
    <button
      mat-flat-button
      color="primary"
      type="button"
      class="sr-pop-save"
      data-testid="special-requests-popup-save"
      [disabled]="saving()"
      [attr.aria-busy]="saving()"
      (click)="save()"
    >
      {{ (saving() ? 'Saving…' : 'Save') | translate }}
    </button>
  </div>
</ng-template>
